import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import RecommendationsList from './components/RecommendationsList/RecommendationsList'
import { AIRiskReportRenderModeProvider } from './components/RenderMode/AIRiskReportRenderModeProvider'
import SectionHeading from './components/SectionHeading/SectionHeading'
import { AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES, RECOMMENDATIONS_ANCHOR } from './data/constants'
import { AI_RISK_REPORT_RENDER_MODE_VALUES, DEFAULT_AI_RISK_REPORT_RENDER_MODE } from './data/renderMode'
import { AI_RISK_REPORT_CHANNEL_VALUES, DEFAULT_AI_RISK_REPORT_CHANNEL } from './data/reportChannel'
import { buildRecommendationItems } from './data/recommendations'
import { buildScoringPresentation } from './data/scoring'
import DataExposure from './sections/DataExposure/DataExposure'
import DeploymentReadiness from './sections/DeploymentReadiness/DeploymentReadiness'
import IdentityAccess from './sections/IdentityAccess/IdentityAccess'
import Overview from './sections/Overview/Overview'
import Reference from './sections/Reference/Reference'
import ThirdPartyAi from './sections/ThirdPartyAi/ThirdPartyAi'
import styles from './AIRiskReport.module.scss'

export default function AIRiskReportRenderer({
  report,
  channel = DEFAULT_AI_RISK_REPORT_CHANNEL,
  renderMode = DEFAULT_AI_RISK_REPORT_RENDER_MODE,
}) {
  const {
    summary = {},
    findings = [],
    provenanceSummary = {},
    dataAvailability = [],
    data = {},
    meta = {},
    scoring = {},
    recommendations: serverRecommendations = [],
  } = report
  const scoringPresentation = useMemo(() => buildScoringPresentation(scoring), [scoring])
  const recommendations = useMemo(() => buildRecommendationItems(serverRecommendations), [serverRecommendations])

  return (
    <AIRiskReportRenderModeProvider renderMode={renderMode} channel={channel}>
      <div className={`${styles.page} w-100 mx-auto`}>
        <div {...AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES}>
          <Overview
            summary={summary}
            scoring={scoringPresentation}
            recommendations={recommendations}
            findings={findings}
          />
        </div>

        <DeploymentReadiness
          summary={summary}
          data={data}
          scoring={scoringPresentation}
          dataAvailability={dataAvailability}
        />
        <DataExposure summary={summary} data={data} scoring={scoringPresentation} dataAvailability={dataAvailability} />
        <IdentityAccess
          summary={summary}
          data={data}
          scoring={scoringPresentation}
          dataAvailability={dataAvailability}
        />
        <ThirdPartyAi
          summary={summary}
          data={data}
          scoring={scoringPresentation}
          findings={findings}
          dataAvailability={dataAvailability}
        />

        <div {...AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES}>
          <SectionHeading id={RECOMMENDATIONS_ANCHOR} titleId="AI_RISK_REPORT.RECOMMENDATIONS" keepWithNext />
          <RecommendationsList items={recommendations} />
          <Reference meta={meta} provenanceSummary={provenanceSummary} />
        </div>
      </div>
    </AIRiskReportRenderModeProvider>
  )
}

const reportPropType = PropTypes.shape({
  summary: PropTypes.shape({}),
  findings: PropTypes.arrayOf(PropTypes.shape({})),
  provenanceSummary: PropTypes.shape({}),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
  data: PropTypes.shape({}),
  meta: PropTypes.shape({}),
  scoring: PropTypes.shape({
    modelVersion: PropTypes.number,
    overall: PropTypes.shape({
      current: PropTypes.shape({ value: PropTypes.number, band: PropTypes.string }),
      afterRecommendations: PropTypes.shape({ value: PropTypes.number, band: PropTypes.string }),
      afterRecommendationsAndLicenses: PropTypes.shape({ value: PropTypes.number, band: PropTypes.string }),
    }),
    sections: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  recommendations: PropTypes.arrayOf(PropTypes.shape({})),
})

AIRiskReportRenderer.propTypes = {
  report: reportPropType.isRequired,
  channel: PropTypes.oneOf(AI_RISK_REPORT_CHANNEL_VALUES),
  renderMode: PropTypes.oneOf(AI_RISK_REPORT_RENDER_MODE_VALUES),
}
